import { useEffect, useRef } from "react"
import { Mention, MentionBlot } from "quill-mention"
import "quill-mention/dist/quill.mention.css"
import Quill, { Delta, QuillOptions } from "quill"
Quill.register({ "blots/mention": MentionBlot, "modules/mention": Mention })
import { getUserFromId } from "@pma/utils/helpers"
import useTranslator from "@pma/hooks/use-translator"

type RichEditorQuillProps = {
  handleChange: (e: Delta) => void
  placeHolder?: string
  id: string
  mentionUsers: any[]
  value?: string | undefined
}

const toolbarOptions = {
  container: [
    [{ list: "ordered" }, { list: "bullet" }],
    ["code-block", "link", "image"],
  ],
}

// RTL styles for quill-mention
const rtlStyles = `
  .ql-mention-list-container.rtl-mention {
    direction: rtl;
    text-align: right;
    right: 0px !important
  }

  .ql-mention-list-container.rtl-mention .ql-mention-list-item {
    direction: rtl;
    text-align: right;
    padding: 4px 8px;
  }

  .ql-mention-list-container.rtl-mention .ql-mention-list-item > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    direction: rtl;
  }

  .ql-editor.ql-rtl .mention {
    direction: rtl;
    unicode-bidi: embed;
  }

  .ql-container.ql-rtl {
    direction: rtl;
    text-align: right;
  }

  .ql-container.ql-rtl .ql-editor {
    direction: rtl;
    text-align: right;
  }
`

function RichEditorQuill({
  id,
  mentionUsers,
  placeHolder = "",
  value,
  handleChange,
}: RichEditorQuillProps) {
  const quillRef = useRef<Quill | null>(null)
  const styleInjectedRef = useRef<boolean>(false)
  const { locale} = useTranslator()
  const isHebrew = locale === "he"

  // Inject RTL styles once
  useEffect(() => {
    if (!styleInjectedRef.current) {
      const styleElement = document.createElement("style")
      styleElement.textContent = rtlStyles
      document.head.appendChild(styleElement)
      styleInjectedRef.current = true
    }
  }, [])

  const getUser = () => {
    return value?.replace(/@\d+#/g, (match) => {
      const userId = match?.slice(1, -1)
      const user: any = userId && getUserFromId(userId, mentionUsers)

      if (user?.value) {
        return ` <span class="mention" data-index="0" data-denotation-char="@" data-id={${user.id}} data-value={${user.value}}>&#xFEFF;<span contenteditable="false">@${user.value}</span>&#xFEFF;</span>`
      }

      return match
    })
  }

  const getDefaultValue = (value: string | undefined) => {
    if (value) {
      try {
        let arr = []
        arr = JSON.parse(value)
        if (typeof arr === "string") {
          arr = JSON.parse(arr)
        }
        return arr || ""
      } catch (error) {
        const ql = quillRef.current
        if (ql) {
          const test = getUser()

          if (test) {
            ql.root.innerHTML = ` <p>${test}</p>`
          }
        }

        return value
      }
    }
    return value
  }

  const mentionOptions = {
    // Added Hebrew character range (\u0590-\u05FF)
    allowedChars: /^[A-Za-z\sÅÄÖåäö\u0590-\u05FF]*$/,
    mentionDenotationChars: ["@"],

    source: function (searchTerm: any, renderList: any, mentionChar: any) {
      let values: any[] = []

      if (mentionChar === "@") {
        values = mentionUsers
      }

      if (searchTerm.length === 0) {
        renderList(values, searchTerm)
      } else {
        const matches = []
        for (let i = 0; i < values.length; i++)
          if (~values[i].value.toLowerCase().indexOf(searchTerm.toLowerCase()))
            matches.push(values[i])

        renderList(matches, searchTerm)
      }
    },
    renderItem: function (item: any, searchTerm: any) {
      const container = document.createElement("div")
      container.style.display = "flex"
      container.style.alignItems = "center"
      container.style.padding = "4px"
      container.style.direction = isHebrew ? "rtl" : "ltr"
      container.style.flexDirection = "row"
      container.style.width = "100%"

      const img = document.createElement("img")
      img.src = item.url
      img.style.width = "30px"
      img.style.height = "30px"
      img.style.borderRadius = "50%"
      img.style.flexShrink = "0"
      img.style.marginInlineEnd = "10px"

      const span = document.createElement("span")
      span.textContent = item.value
      span.style.textAlign = isHebrew ? "right" : "left"
      span.style.direction = isHebrew ? "rtl" : "ltr"

      container.appendChild(img)
      container.appendChild(span)

      return container
    },
    // Add RTL class to mention container when in RTL mode
    onOpen: function () {
      if (isHebrew) {
        setTimeout(() => {
          const mentionContainers = document.querySelectorAll(
            ".ql-mention-list-container"
          )
          mentionContainers.forEach((el) => {
            el.classList.add("rtl-mention")
          })
        }, 0)
      }
    },
  }

  useEffect(() => {
    const container = document.getElementById(id)
    if (container) {
      const quillModules: Record<string, any> = {
        toolbar: toolbarOptions,
        mention: mentionOptions,
      }
      const quillOptions: QuillOptions = {
        theme: "snow",
        modules: quillModules,
        placeholder: placeHolder,
      }
      quillRef.current = new Quill(container, quillOptions)

      // Apply RTL to Quill container
      if (isHebrew) {
        const qlContainer = container.querySelector(".ql-container")
        const qlEditor = container.querySelector(".ql-editor")
        
        if (qlContainer) {
          qlContainer.classList.add("ql-rtl")
          ;(qlContainer as HTMLElement).style.direction = "rtl"
          ;(qlContainer as HTMLElement).style.textAlign = "right"
        }
        
        if (qlEditor) {
          ;(qlEditor as HTMLElement).style.direction = "rtl"
          ;(qlEditor as HTMLElement).style.textAlign = "right"
        }
      }

      if (value) {
        const convertedValue = getDefaultValue(value)
        quillRef.current.setContents(convertedValue)
      }
    }
  }, [])

  const handleEditorChange = () => {
    const content = quillRef.current?.getContents()

    if (content) handleChange(content)
  }

  useEffect(() => {
    if (!quillRef?.current) {
      return
    }
    quillRef.current.on("editor-change", handleEditorChange)

    // Cleanup listener on unmount
    return () => {
      quillRef.current?.off("editor-change", handleEditorChange)
    }
  }, [quillRef.current])

  useEffect(() => {
    if (!quillRef?.current) {
      return
    }

    if(value == "") {
        const convertedValue = getDefaultValue(value)
        quillRef.current.setContents(convertedValue)
    }
  }, [value])

  // Update placeholder when translations load
  useEffect(() => {
    if (quillRef.current && placeHolder) {
      quillRef.current.root.dataset.placeholder = placeHolder
    }
  }, [placeHolder])

  return (
    <div dir={isHebrew ? "rtl" : "ltr"} style={{ direction: isHebrew ? "rtl" : "ltr" }}>
      <div id={id}></div>
    </div>
  )
}

export default RichEditorQuill